import { BaseOnboardingSection } from "../../../../../pages/onboarding-client/sections/base-onboarding-section";
import { OnboardingClientService } from "../../../../../../services/onboarding/onboarding-client.service";
import { BindingSignaler } from "aurelia-templating-resources";
import { OnboardingSession } from "../../../../../pages/onboarding-client/onboarding.session";
import { ArrayUtils } from "../../../../../../utilities/arrays";
import { FileHelper } from "../../../../../../helpers/file-helper";
import { autoinject, transient, computedFrom } from "aurelia-framework";
import { NotificationType, NotificationClientService } from "../../../../../../services/basics/notification-client.service";
import { AttachmentHelper } from "@rightship-tech/common-ui/helpers/attachment.helper";
import { FormFileWithType, OnboardingRequestDocumentModel, OnboardingRequiredDocumentTypeModel, OnboardingRequestModel, UploadDocumentsRequest } from "../../../../../../models/onboarding/onboarding.models";
import { FormAttachmentAttributes } from "../../../../../../models/onboarding/rightship-onboarding.models";

@autoinject()
@transient()
export class AttachmentsSection extends BaseOnboardingSection {

    documents: FormFileWithType[] = [];
    allDocumentAlreadyUploaded: OnboardingRequestDocumentModel[] = [];
    documentAlreadyUploaded: OnboardingRequestDocumentModel[] = [];
    documentsToRemove: number[] = [];
    requiredDocumentTypes: OnboardingRequiredDocumentTypeModel[];
    model: any;
    inputHiddenElements: HTMLInputElement[] = [];
    documentAttributes: { [onboardingRequestDocumentId: number]: FormAttachmentAttributes } = {};

    @computedFrom('onboardingSession', 'model')
    get onboardingRequestGuid(): string {
        return this.onboardingSession.onboardingRequestGuid || (this.model.onboardingRequest ? this.model.onboardingRequest.onboardingRequestGuid : null);
    }

    constructor(private onboardingClientService: OnboardingClientService,
        private bindingSignaler: BindingSignaler,
        private notificationService: NotificationClientService,
        private onboardingSession: OnboardingSession) {
        super();
    }

    activate(model: { onboardingRequest: OnboardingRequestModel, readonly: boolean, guid: string, formAttachmentAttributes: FormAttachmentAttributes[]}) {

        super.activate(model);
        this.model = model;

        this.allDocumentAlreadyUploaded = model.onboardingRequest ? model.onboardingRequest.uploadedDocuments : this.onboardingSession.getDocuments();
        if (!this.allDocumentAlreadyUploaded) {
            this.allDocumentAlreadyUploaded = [];
        }

        this.documentAlreadyUploaded = this.allDocumentAlreadyUploaded.filter(z => !this.guid || z.groupingGuid == this.guid);

        this.documentsToRemove = [];
        this.documents = [];

        if (this.guid) {

            this.onboardingClientService.getRequiredDocumentTypes({
                guid: model.onboardingRequest ? null : this.onboardingSession.guid,
                onboardingRequestGuid: this.onboardingRequestGuid
            }).then(x => {
                this.requiredDocumentTypes = x.requiredDocumentTypes;
                const validAttachmentTypes = this.requiredDocumentTypes.map(x => x.attachmentTypeId);
                const documentsToRemove: OnboardingRequestDocumentModel[] = [];
                this.documentAlreadyUploaded.forEach(doc => {
                    if (validAttachmentTypes.indexOf(doc.attachmentTypeId) == -1) {
                        documentsToRemove.push(doc);
                    }
                });
                this.documentsToRemove.push(...documentsToRemove.map(x => x.onboardingRequestDocumentId));
            });
        } else {
            this.requiredDocumentTypes = [{
                attachmentTypeId: null,

            }];
        }

        if (model.formAttachmentAttributes) {
            model.formAttachmentAttributes.forEach(a => {
                this.documentAttributes[a.onboardingRequestDocumentId] = a;
            });
        }
    }

    hasFiles(docTypeId: number): boolean {
        return !(this.documentAlreadyUploaded.filter(x => x.attachmentTypeId == docTypeId).length == 0 && this.documents.filter(x => x.attachmentTypeId == docTypeId).length == 0);
    }

    filterDocs(documents: (OnboardingRequestDocumentModel | FormFileWithType)[], docTypeId: number) {
        if (!documents) return [];

        return documents.filter(x => x.attachmentTypeId == docTypeId);        
    }

    onFileSelected(elementIndex, docTypeId: number) {
        const file = event.target["files"] ? event.target["files"].item(0) : event["detail"].files.item(0);

        if (event["detail"] && event["detail"].innerEvent) {
            event["detail"].innerEvent.target.value = '';
        }
        const selectedAttachmentTypeId = docTypeId;
        if (file) {
            if (!this.documents) {
                this.documents = [];
            }

            if (this.documents.filter(x => x.file.name == file.name && x.attachmentTypeId == selectedAttachmentTypeId).length == 0 &&
                this.documentAlreadyUploaded.filter(x => x.documentName == file.name && x.attachmentTypeId == selectedAttachmentTypeId).length == 0) {
                    const document: FormFileWithType = { file: file, attachmentTypeId: selectedAttachmentTypeId, groupingGuid: this.guid, documentName: file.name };
                    this.documents.push(document);
            }
        }

        this.inputHiddenElements[elementIndex].value = null;
        this.bindingSignaler.signal('update-has-files');
    }

    removeDocument($event, document: FormFileWithType | OnboardingRequestDocumentModel) {

        $event.stopPropagation();

        if (this.instanceOfFormFileWithType(document)) {
            const index = ArrayUtils.findIndex(this.documents, x => x == document);
            if (index > -1) {
                this.documents.splice(index, 1);
            }
        } else {
            let index = ArrayUtils.findIndex(this.documentAlreadyUploaded, x => x == document);
            if (index > -1) {
                this.documentAlreadyUploaded.splice(index, 1);

                if (document.onboardingRequestGuid == this.onboardingRequestGuid) {
                    this.documentsToRemove.push(document.onboardingRequestDocumentId);
                }
            }
            index = ArrayUtils.findIndex(this.allDocumentAlreadyUploaded, x => x == document);
            if (index > -1) {
                this.allDocumentAlreadyUploaded.splice(index, 1);
            }
        }

        this.bindingSignaler.signal('update-has-files');
    }

    instanceOfFormFileWithType(object: any): object is FormFileWithType {
        return 'attachmentTypeId' in object && 'file' in object;
    }

    generateUploadDocumentRequest(): UploadDocumentsRequest {
        const request = {
            documents: this.getUniqueDocuments(this.documentAlreadyUploaded.filter(x => x.onboardingRequestGuid != this.onboardingRequestGuid), this.documents),
            onboardingRequestGuid: this.onboardingRequestGuid,
            guid: this.onboardingSession.guid,
            modelMeta: this.onboardingSession.modelMeta,
            documentsToRemove: this.documentsToRemove
        };

        return request;
    }

    getUniqueDocuments(onboardingRequestDocumentModel: OnboardingRequestDocumentModel[], documents: FormFileWithType[]) {
        const newDocuments =
            documents.filter(x => onboardingRequestDocumentModel.filter(y => y.attachmentTypeId == x.attachmentTypeId && y.documentName == x.documentName).length == 0);

        return onboardingRequestDocumentModel.concat(newDocuments);
    }

    validate(request: UploadDocumentsRequest): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.onboardingClientService.validateUploadDocuments(request).then(resolve).catch(reject);
        });
    }

    saveAndValidate(isValidate: boolean): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const request = this.generateUploadDocumentRequest();

            if (isValidate) {
                this.validate(request).then(() => {
                    this.saveDocuments(request).then(resolve).catch(reject);
                }).catch(reject);
            }
            else {
                this.saveDocuments(request).then(resolve).catch(reject);
            }
        });
    }

    saveDocuments(request?: UploadDocumentsRequest) {
        if (!request) {
            request = this.generateUploadDocumentRequest();
        }

        return new Promise<void>((resolve, reject) => {

            //avoid the call to the backend if you dont have any documents to save
            if ((!request.documents || request.documents.length == 0) &&
                (!request.documentsToRemove || request.documentsToRemove.length == 0)) {
                resolve();
            }
            else {
                this.onboardingClientService.uploadDocuments(request).then(response => {

                    this.onboardingSession.updateSession(response.onboardingRequestGuid);

                    this.onboardingSession.storeDocuments(response.uploadedDocuments);

                    this.refreshDocuments();
                    this.documents = this.documents.filter(x => request.documents.indexOf(x) === -1);

                    ArrayUtils.removeIf(this.allDocumentAlreadyUploaded, x => x.onboardingRequestGuid != response.onboardingRequestGuid);
                    resolve();
                }).catch(() => {
                    reject();
                });
            }
        });
    }

    refreshDocuments() {
        this.allDocumentAlreadyUploaded = this.onboardingSession.getDocuments();
        if (!this.allDocumentAlreadyUploaded) {
            this.allDocumentAlreadyUploaded = [];
        }

        this.documentAlreadyUploaded = this.allDocumentAlreadyUploaded.filter(z => !this.guid || z.groupingGuid == this.guid);
    }



    openDocument(document: OnboardingRequestDocumentModel) {
        if (document && document.documentPath) {
            let path = AttachmentHelper.getUrl(document.documentPath);
            const downloadFile = !(this.isPDF(path) || this.isImage(path));

            //Get the SAS token for the document
            this.onboardingClientService.getSharedAccessSignatureToken(document.onboardingRequestDocumentId, downloadFile).then(sasToken => {

                if (sasToken) {
                    path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                }

                if (!downloadFile) {
                    this.getAttachmentAndOpenNewTab(document.documentName, path);
                }
                else {
                    FileHelper.downloadFile(AttachmentHelper.getUrl(path), document.documentName);
                }
            }).catch(() => {
                this.notificationService.show("Error", "You do not have enough permissions to open/download this file", NotificationType.Error);
            });
        }
    }

    public isPDF(link): boolean {
        return link.indexOf('.pdf') != -1;
    }

    public isImage(link): boolean {
        if(link) {
            const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp"];
            return imageExtensions.some(x => link?.indexOf(x) != -1);
        }
        else { 
            return false;
        }
    }

    public async getPreview(document: OnboardingRequestDocumentModel | FormFileWithType) {

        if (document?.documentPath && "onboardingRequestDocumentId" in document) {
            let path = AttachmentHelper.getUrl(document.documentPath);            
            if (!this.isImage(path)) null;

            const sasToken = await this.onboardingClientService.getSharedAccessSignatureToken(document.onboardingRequestDocumentId, false);
            
            if (sasToken) {
                path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
            }    

            return path;
        }
        else if (document && "file" in document) {
            return document.file;
        }
        else return null;        
    }

    public save: () => Promise<any> = () => {
        return null;
    }

    public saveOverride = (isValidate: boolean): Promise<any> => {
        return this.saveAndValidate(isValidate);
    }

    downloadSample(path: string, fileName: string) {
        FileHelper.downloadFile(AttachmentHelper.getUrl(path), fileName);
    }

    public nameWithoutExtension(fileName: string) {
        return fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
    }

    public attachmentExtension(fileName: string) {
        return fileName.substring(fileName.lastIndexOf('.')+1, fileName.length).toUpperCase() || 'N/a';
    }

    public getAttachmentAndOpenNewTab(attachmentName: string, attachmentPath: string) {

        // attachmentPath = attachmentPath.replace('localhost:5011', 'storage-sq1.rightship.com');
        const request = new XMLHttpRequest();
        request.open('GET', attachmentPath, true);
        request.responseType = 'blob'
        request.onload = function() {
            const reader = new FileReader();
            reader.readAsDataURL(request.response);
            reader.onload = function(e) {

                const dataURI = e.target.result;
                const byteString = atob((dataURI as string).split(',')[1]);
                // separate out the mime component
                const mimeString = (dataURI as string).split(',')[0].split(':')[1].split(';')[0]
                // write the bytes of the string to an ArrayBuffer
                const ab = new ArrayBuffer(byteString.length);
                // create a view into the buffer
                const ia = new Uint8Array(ab);
                // set the bytes of the buffer to the correct values
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                // write the ArrayBuffer to a blob, and you're done
                const blob = new Blob([ab], {type: mimeString});

                //Check if attachment is an image
                const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg", "webp"];
                const isImage = imageExtensions.some(x => attachmentPath?.indexOf(x) != -1);

                if(isImage) {
                    const win = window.open('', '_blank'),
                        img = document.createElement('img'),
                        title = document.createElement('title'),
                        fileUrl = URL.createObjectURL(blob);
                    title.appendChild(document.createTextNode(attachmentName));
                    img.src = fileUrl;
                    win.document.head.appendChild(title);
                    win.document.body.appendChild(img);
                    win.document.body.style.margin = '0';
                }
                else {
                    const win = window.open('', '_blank'),
                        iframe = document.createElement('iframe'),
                        title = document.createElement('title'),
                        fileUrl = URL.createObjectURL(blob);
                    title.appendChild(document.createTextNode(attachmentName));
                    iframe.src = fileUrl;
                    iframe.width = '100%';
                    iframe.height = '100%';
                    iframe.style.border = 'none';
                    if (win) {
                        win.document.head.appendChild(title);
                        win.document.body.appendChild(iframe);
                        win.document.body.style.margin = '0';
                    }
                }
            };
        };
        request.send();
    }
}